import type { ColumnFiltersState, SortingState, VisibilityState } from '@tanstack/react-table';

export const DEFAULT_TABLE_PAGE_SIZE = 25;
export const ALLOWED_TABLE_PAGE_SIZES = [25, 50, 75, 100] as const;

const toNumber = (value: unknown) => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (Array.isArray(value) && value.length > 0) {
    return toNumber(value[0]);
  }
  return undefined;
};

export function parseTablePageParam(value: unknown) {
  const parsed = toNumber(value);
  if (parsed === undefined || parsed < 1) {
    return 1;
  }
  return Math.floor(parsed);
}

export function parseTablePageSizeParam(value: unknown) {
  const parsed = toNumber(value);
  if (parsed === undefined) {
    return DEFAULT_TABLE_PAGE_SIZE;
  }
  const pageSize = Math.floor(parsed);
  return (ALLOWED_TABLE_PAGE_SIZES as readonly number[]).includes(pageSize) ? pageSize : DEFAULT_TABLE_PAGE_SIZE;
}

/**
 * Converts column filters URL query parameters to a column filters state
 *
 * @example
 * columnFiltersParamToState({createdBy: "Neil", tags: "Tag 1"}) => [{id: "createdBy", value: "Neil"}, {id: "tags", value: "Tag 1"}]
 */
export function columnFiltersParamToState(filterParams: Record<string, string>) {
  return Object.entries(filterParams)
    .filter(([_, value]) => Boolean(value))
    .map(([key, value]) => ({ id: key, value }));
}

/**
 * Converts a column filters state to URL query parameters
 *
 * @example
 * columnFiltersStateToParam([{id: "createdBy", value: "Neil"}, {id: "tags", value: "Tag 1"}], ["createdBy", "tags"])
 * => {createdBy: "Neil", tags: "Tag 1"}
 */
export function columnFiltersStateToParam(columnFilters: ColumnFiltersState, filterableColumns: string[]) {
  return Object.fromEntries(
    filterableColumns.map((columnId) => [
      columnId,
      columnFilters.find((filter) => filter.id === columnId)?.value as string,
    ]),
  );
}

/**
 * Converts a column visibility URL query parameter string to a state object
 *
 * @example
 * visibilityParamToState("createdBy,tags") => {createdBy: false, tags: false}
 */
export function visibilityParamToState(visibility: string): VisibilityState {
  return visibility ? Object.fromEntries(visibility.split(',').map((columnId) => [columnId, false])) : {};
}

/**
 * Converts a column visibility state object to a URL query parameter string
 *
 * @example
 * visibilityStateToParam({createdBy: false, tags: false}) => "createdBy,tags"
 */
export function visibilityStateToParam(visibility: VisibilityState) {
  return Object.entries(visibility)
    .filter(([_, isVisible]) => !isVisible)
    .map(([columnId]) => columnId)
    .join(',');
}

/**
 * Converts sorting URL query parameters to a sorting state
 *
 * @example
 * sortingParamToState("createdAt", "desc") => [{id: "createdAt", desc: true}]
 */
export function sortingParamToState(sortBy: string, sortDirection: string): SortingState {
  return sortBy ? [{ id: sortBy, desc: sortDirection === 'desc' }] : [];
}

/**
 * Converts a sorting state to URL query parameters
 *
 * @example
 * sortingStateToParam([{id: "createdAt", desc: true}]) => {sortBy: "createdAt", sortDirection: "desc"}
 */
export function sortingStateToParam(sorting: SortingState) {
  const firstSortRule = sorting[0];
  return firstSortRule ?
      { sortBy: firstSortRule.id, sortDirection: firstSortRule.desc ? 'desc' : 'asc' }
    : { sortBy: '', sortDirection: '' };
}
